import type { AdminUser } from './auth-store';

const domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;
const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Passwordless email OTP connection — the admin panel and the mobile/web app
// share this Auth0 connection. Using the embedded flow (POST /passwordless/start
// + /oauth/token) sidesteps the hosted Universal Login, which ignores
// `connection=email` on this tenant and falls back to the database password
// widget (engram #737).
const CONNECTION = 'email';

export type AdminAuthErrorCode =
  | 'rate_limited'
  | 'invalid_email'
  | 'connection_disabled'
  | 'invalid_code'
  | 'not_provisioned'
  | 'unknown';

export class AdminAuthError extends Error {
  readonly code: AdminAuthErrorCode;

  constructor(code: AdminAuthErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'AdminAuthError';
    this.code = code;
  }
}

export interface AdminSession {
  token: string;
  user: AdminUser;
}

function mapStartError(body: {
  error?: string;
  error_description?: string;
}): AdminAuthErrorCode {
  const raw = (body?.error ?? '').toString().toLowerCase();
  const description = (body?.error_description ?? '').toString().toLowerCase();

  if (raw.includes('rate') || description.includes('rate') || description.includes('too many')) {
    return 'rate_limited';
  }
  if (description.includes('disabled') || description.includes('connection')) {
    return 'connection_disabled';
  }
  if (
    raw.includes('bad.email') ||
    raw.includes('invalid_request') ||
    description.includes('invalid email') ||
    description.includes('valid email') ||
    description.includes('email address')
  ) {
    return 'invalid_email';
  }
  return 'unknown';
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * Ask Auth0 to email a one-time code to `email`. Does NOT log the user in;
 * `verifyOtpCode` completes the login once the emailed code is submitted.
 */
export async function sendOtpCode(email: string): Promise<void> {
  const response = await fetch(`https://${domain}/passwordless/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id: clientId,
      connection: CONNECTION,
      email,
      send: 'code',
    }),
  });

  if (!response.ok) {
    throw new AdminAuthError(mapStartError(await readJson(response)));
  }
}

/**
 * Exchange the emailed OTP for an Auth0 access token, then trade that token
 * for an admin JWT via the backend `/admin/auth/exchange` guard.
 */
export async function verifyOtpCode(email: string, code: string): Promise<AdminSession> {
  const tokenResponse = await fetch(`https://${domain}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'http://auth0.com/oauth/grant-type/passwordless/otp',
      client_id: clientId,
      username: email,
      otp: code,
      realm: CONNECTION,
      audience,
      scope: 'openid profile email',
    }),
  });

  if (!tokenResponse.ok) {
    throw new AdminAuthError('invalid_code');
  }

  const { access_token: accessToken } = (await readJson(tokenResponse)) as {
    access_token?: string;
  };

  if (!accessToken) {
    throw new AdminAuthError('unknown');
  }

  const exchangeResponse = await fetch(`${apiUrl}/api/v1/admin/auth/exchange`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (exchangeResponse.status === 403) {
    const body = await readJson(exchangeResponse);
    if (body?.code === 'NOT_PROVISIONED') {
      throw new AdminAuthError('not_provisioned');
    }
    throw new AdminAuthError('unknown');
  }

  if (!exchangeResponse.ok) {
    throw new AdminAuthError('unknown');
  }

  const data = (await readJson(exchangeResponse)) as {
    token: string;
    user: AdminUser;
  };

  return {
    token: data.token,
    user: {
      id: data.user.id,
      email: data.user.email,
      fullName: data.user.fullName,
      platformRole: data.user.platformRole,
    },
  };
}
